import React, { ReactNode } from 'react';
import classNames from 'classnames';

type Props = {
  items?: 'stretch' | 'start' | 'center' | 'end';
  justify?: 'start' | 'center' | 'end' | 'between' | 'around';
  flex?: 'initial' | '1' | 'auto';
  spacing?: number;
  wrap?: boolean;
  vertical?: boolean;
  className?: string;
};

export const Stack: React.FC<Props> = ({
  children,
  items = 'stretch',
  justify = 'start',
  spacing = 4,
  wrap = true,
  vertical = false,
  flex = 'initial',
  className,
}) => (
  <div
    className={classNames(
      'flex',
      // Due to purging of styles, classNames below should cover all used
      // values of 'items', 'justify' and 'spacing'
      // https://tailwindcss.com/docs/optimizing-for-production#writing-purgeable-html
      {
        'items-stretch': items === 'stretch',
        'items-start': items === 'start',
        'items-center': items === 'center',
        'items-end': items === 'end',
      },
      {
        'justify-start': justify === 'start',
        'justify-center': justify === 'center',
        'justify-end': justify === 'end',
        'justify-between': justify === 'between',
        'justify-around': justify === 'around',
      },
      {
        '-mt-1 -ml-1': spacing === 1,
        '-mt-2 -ml-2': spacing === 2,
        '-mt-4 -ml-4': spacing === 4,
        '-mt-6 -ml-6': spacing === 6,
        '-mt-8 -ml-8': spacing === 8,
      },
      {
        'flex-wrap': wrap,
        'flex-col': vertical,
      },
      className
    )}
  >
    {React.Children.map(
      children as any,
      (child: React.ReactElement<ReactNode>, index) => {
        if (child === null) {
          return null;
        }

        return (
          <div
            key={index}
            className={classNames(
              'min-w-0 max-w-full',
              // Due to purging of styles, classNames below should cover all used
              // values of 'flex' and 'spacing'
              // https://tailwindcss.com/docs/optimizing-for-production#writing-purgeable-html
              {
                'flex-initial': flex === 'initial',
                'flex-1': flex === '1',
                'flex-auto': flex === 'auto',
              },
              {
                'mt-1 ml-1': spacing === 1,
                'mt-2 ml-2': spacing === 2,
                'mt-4 ml-4': spacing === 4,
                'mt-6 ml-6': spacing === 6,
                'mt-8 ml-8': spacing === 8,
              }
            )}
          >
            {child}
          </div>
        );
      }
    )}
  </div>
);
